/**
 * Notices for what happens in the room, the way other meeting apps announce it.
 *
 * Each of these was already tracked — a hand icon on a tile, a share on the
 * stage, a role in the roster, a row in the lobby widget — but changed without
 * a word, so anyone not looking at the right spot missed it.
 *
 *   room (in-call "Notifications" switch):  X raised their hand · X started /
 *     stopped presenting (from the presenter's own share events) · X stopped recording · cloud recording stopped ·
 *     X is waiting to join (moderators, with a sound) · meeting locked/unlocked
 *     (moderators) · Y made X a co-host / let X present, and the reverse
 *     (moderators, when another moderator did it)
 *   you (always shown):  you are now the host / a co-host / no longer one ·
 *     you can / can no longer present
 *
 * With several co-hosts in a room, each of these names the moderator who did
 * it — "Asha made you a co-host", "Asha locked the meeting" — whenever the room
 * says who (services/moderator-notices).
 *
 * Everything is a transition from what was already known, so joining a room
 * where hands are up, someone is sharing or the lobby has a queue announces
 * none of it. Roles, lock and lobby are only compared once the first state has
 * settled — `roles:get` and the lobby fetch both land a moment after joining,
 * and that first answer is not a change.
 */
import { useEffect, useRef } from 'react'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import type { LobbyRequest } from '@/features/meetings/services/meetings.api'
import { meetingAlert } from '../services/meeting-alert'
import { usePresenterRequestsStore } from '../store/presenter-requests.store'
import { useMeetingPreferencesStore } from '../store/meeting-preferences.store'
import { playNotificationSound } from '@vc/utils/notificationSound'
import type { RoomRole } from './useRoomRoles'
import type { PhoenixEventHandler } from './usePhoenixChannel'
import { isMediaOnlyPeer } from './useMeetingConnection'
import {
  actedByOther,
  actorName,
  othersRoleNotices,
  selfNoticeByKey,
  type Actor,
  type RoleSnapshot,
} from '../services/moderator-notices'
import {
  presentingStarted,
  presentingStopped,
  presentingSync,
  selfNotice,
  type PresentingSet,
  type SelfStanding,
} from '../services/meeting-notices'

/** How long after joining the first roles / lobby / lock answers are treated as the starting point. */
const SETTLE_MS = 4000

type PeerLike = { peerId: string; name: string; handRaised?: boolean; isSharingScreen?: boolean }

type Args = {
  /** The room channel — presenting is announced from its share events. */
  subscribe: (event: string, handler: PhoenixEventHandler) => () => void
  channelJoined: boolean
  peers: ReadonlyMap<string, PeerLike>
  /** This client's peer id — what `myRole` was read for. */
  selfId: string | null
  myRole: RoomRole
  canPresent: boolean
  isModerator: boolean
  /** The room's roles, for telling moderators what another moderator changed. */
  roles: RoleSnapshot
  /** Who caused the roles as they stand (useRoomRoles `changedBy`). */
  rolesChangedBy: Actor | null
  lobbyRequests: readonly LobbyRequest[]
  locked: boolean
  /** Who locked or unlocked it (useLobby `lockedBy`). */
  lockedBy: Actor | null
  recorderNames: readonly string[]
  cloudRecording: boolean
}

export function useMeetingEventAlerts(a: Args) {
  const t = useTranslation()
  const tRef = useRef(t)
  tRef.current = t

  // Settled once the channel has been joined for SETTLE_MS.
  const settledRef = useRef(false)
  useEffect(() => {
    settledRef.current = false
    if (!a.channelJoined) return
    const id = setTimeout(() => { settledRef.current = true }, SETTLE_MS)
    return () => clearTimeout(id)
  }, [a.channelJoined])

  // Hands, per peer — only someone already in the room changing, never
  // someone arriving with a hand up.
  const peersRef = useRef(new Map<string, PeerLike>())
  // Who is presenting, as far as the room has said (see services/meeting-notices).
  const presentingRef = useRef<PresentingSet>(new Map())
  useEffect(() => {
    const tr = tRef.current
    const next = new Map<string, PeerLike>()
    for (const p of a.peers.values()) {
      next.set(p.peerId, p)
      const before = peersRef.current.get(p.peerId)
      if (!before) continue
      const name = p.name || tr('meetingScreen.participantFallbackName') || 'A participant'
      if (p.handRaised && !before.handRaised) {
        meetingAlert(tr('meetingScreen.alertHandRaised', { name }), { sound: true })
      }
      // A room system (H.323 / SIP / the mixer) sends no share events: its
      // presentation is only ever known from the stream, as before.
      if (!isMediaOnlyPeer(p.peerId)) continue
      if (p.isSharingScreen && !before.isSharingScreen) {
        meetingAlert(tr('meetingScreen.alertStartedPresenting', { name }))
      } else if (!p.isSharingScreen && before.isSharingScreen) {
        meetingAlert(tr('meetingScreen.alertStoppedPresenting', { name }))
      }
    }
    peersRef.current = next
    // Shares received before (or without) hearing them start are learned here
    // without a notice — their `started`, if it is still on its way, is
    // announced when it lands; people who left are forgotten.
    presentingRef.current = presentingSync(
      presentingRef.current,
      new Set(next.keys()),
      [...next.values()].filter((p) => p.isSharingScreen && !isMediaOnlyPeer(p.peerId)).map((p) => p.peerId),
    )
  }, [a.peers])

  // Presenting, from what the presenter said rather than from whether this
  // client happens to hold their stream. Reading it off the stream announced a
  // share that was already running to everyone who joined after it started
  // (the roster row lands before the stream does), and announced every running
  // share as stopped and started again whenever this client's own media
  // session was rebuilt. The presenter sends `started` once per share, however
  // often it republishes, and `stopped` once when it ends.
  const subscribe = a.subscribe
  useEffect(() => {
    const nameOf = (peerId: string) =>
      peersRef.current.get(peerId)?.name || tRef.current('meetingScreen.participantFallbackName') || 'A participant'
    const offStarted = subscribe('media:screenshare_started', (payload) => {
      const peerId = (payload as { peerId?: unknown } | null)?.peerId
      if (typeof peerId !== 'string') return
      const { set, announce } = presentingStarted(presentingRef.current, peerId)
      presentingRef.current = set
      if (announce) meetingAlert(tRef.current('meetingScreen.alertStartedPresenting', { name: nameOf(peerId) }))
    })
    const offStopped = subscribe('media:screenshare_stopped', (payload) => {
      const peerId = (payload as { peerId?: unknown } | null)?.peerId
      if (typeof peerId !== 'string') return
      const { set, announce } = presentingStopped(presentingRef.current, peerId)
      presentingRef.current = set
      if (announce) meetingAlert(tRef.current('meetingScreen.alertStoppedPresenting', { name: nameOf(peerId) }))
    })
    return () => {
      offStarted()
      offStopped()
    }
  }, [subscribe])

  // Who did it, by name — when it was not this client. Read when an update
  // lands, from the same render that carries it.
  const nameOfActor = (by: Actor | null): string | null =>
    actedByOther(by, a.selfId) ? actorName(by, (id) => peersRef.current.get(id)?.name) : null

  // Your own role. Which changes are news is decided in services/meeting-notices
  // (selfNotice) — not the ones that come from leaving.
  const roleRef = useRef<SelfStanding>({ selfId: a.selfId, role: a.myRole, canPresent: a.canPresent })
  useEffect(() => {
    const before = roleRef.current
    const now: SelfStanding = { selfId: a.selfId, role: a.myRole, canPresent: a.canPresent }
    roleRef.current = now
    if (!settledRef.current) return
    const tr = tRef.current
    const notice = selfNotice(before, now)
    // "Asha made you a co-host": with several moderators, which one is the
    // question. The approval of your own request to present keeps its own
    // notice, below.
    const name = nameOfActor(a.rolesChangedBy)
    if (notice && name && notice !== 'can-present') {
      meetingAlert(tr(selfNoticeByKey(notice), { name }), { personal: true })
      return
    }
    switch (notice) {
      case 'you-are-host':
        meetingAlert(tr('meetingScreen.alertYouAreHost'), { personal: true })
        return
      case 'you-are-cohost':
        meetingAlert(tr('meetingScreen.alertYouAreCohost'), { personal: true })
        return
      case 'no-longer-host':
        meetingAlert(tr('meetingScreen.alertNoLongerHost'), { personal: true })
        return
      case 'no-longer-cohost':
        meetingAlert(tr('meetingScreen.alertNoLongerCohost'), { personal: true })
        return
      case 'cannot-present':
        meetingAlert(tr('meetingScreen.alertCannotPresent'), { personal: true })
        return
      case 'can-present':
        // An approved request already says so (dialogs.presenterRequestApproved),
        // and the approval is noticed from this same roles update — let it land.
        setTimeout(() => {
          if (usePresenterRequestsStore.getState().myOutcome === 'approved') return
          meetingAlert(
            name ? tr(selfNoticeByKey('can-present'), { name }) : tr('meetingScreen.alertCanPresent'),
            { personal: true },
          )
        }, 300)
        return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.selfId, a.myRole, a.canPresent])

  // Everyone else's role, for hosts and co-hosts: when one of them makes
  // somebody a co-host or lets them present, the others were told nothing and
  // only found out from a label in the roster.
  const rolesRef = useRef<RoleSnapshot>(a.roles)
  useEffect(() => {
    const before = rolesRef.current
    rolesRef.current = a.roles
    if (!settledRef.current || !a.isModerator) return
    const name = nameOfActor(a.rolesChangedBy)
    if (!name) return
    const tr = tRef.current
    for (const notice of othersRoleNotices(before, a.roles, {
      selfId: a.selfId,
      actorId: a.rolesChangedBy?.id ?? null,
      name,
      present: (id) => peersRef.current.has(id),
      nameOf: (id) => peersRef.current.get(id)?.name || tr('meetingScreen.participantFallbackName'),
    })) {
      meetingAlert(tr(notice.key, notice.vars), { personal: notice.personal })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.roles.byPeer, a.roles.presenters])

  // Lobby knocks, for whoever can admit them.
  //
  // The knock itself is shown by the requests pill now (it reads the same
  // waiting list directly), so this effect no longer raises a notice for it.
  // The sound stays: the pill stays closed until the host opens it, and the
  // sound is what gets their attention while they are looking elsewhere.
  const lobbyRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const before = lobbyRef.current
    lobbyRef.current = new Set(a.lobbyRequests.map((r) => r.id))
    if (!settledRef.current || !a.isModerator) return
    const arrived = a.lobbyRequests.filter((r) => !before.has(r.id))
    if (arrived.length === 0) return
    const prefs = useMeetingPreferencesStore.getState()
    if (prefs.inMeetingNotifications && prefs.inMeetingNotificationSounds) playNotificationSound()
  }, [a.lobbyRequests, a.isModerator])

  // Lock — known to moderators only.
  const lockedRef = useRef(a.locked)
  useEffect(() => {
    const before = lockedRef.current
    lockedRef.current = a.locked
    if (!settledRef.current || !a.isModerator || before === a.locked) return
    const name = nameOfActor(a.lockedBy)
    meetingAlert(
      name
        ? tRef.current(a.locked ? 'meetingScreen.alertMeetingLockedBy' : 'meetingScreen.alertMeetingUnlockedBy', { name })
        : tRef.current(a.locked ? 'meetingScreen.alertMeetingLocked' : 'meetingScreen.alertMeetingUnlocked'),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.locked, a.isModerator])

  // Recording stopping. Starting is already announced (MeetingScreen).
  const recordersRef = useRef<readonly string[]>(a.recorderNames)
  useEffect(() => {
    const before = recordersRef.current
    recordersRef.current = a.recorderNames
    for (const name of before) {
      if (!a.recorderNames.includes(name)) {
        meetingAlert(tRef.current('meetingScreen.alertStoppedRecording', { name }), { personal: true })
      }
    }
  }, [a.recorderNames])
  const cloudRef = useRef(a.cloudRecording)
  useEffect(() => {
    const before = cloudRef.current
    cloudRef.current = a.cloudRecording
    if (before && !a.cloudRecording) meetingAlert(tRef.current('meetingScreen.alertCloudRecordingStopped'), { personal: true })
  }, [a.cloudRecording])
}
